import React, { useRef } from 'react';
import { Platform, StyleSheet, View, type GestureResponderEvent } from 'react-native';
import { colorFor } from '../../../utils/itemPalette';
import {
  type DragKind,
  type PointerEventLike,
  useBoxDraw,
} from '../hooks/useBoxDraw';
import type { AnnotationPoint } from '../../../api/scanStreamTypes';
import type { Bbox, ImageTransform, Point } from '../useImageTransform';

/**
 * Editable bbox overlay for refine mode.
 *
 * Two sub-modes — the parent owns which one is active:
 *   - mode='box':    drag body + 8 resize handles. use_box_as_mask
 *                    commit on Apply.
 *   - mode='points': bbox is rendered read-only; a full-area tap
 *                    catcher over the image picks up include/
 *                    exclude points for SAM3 to re-run against.
 *
 * Touch-first: handles are 28px targets (visual is 14px) to respect
 * HIG minimum. `touch-action: none` prevents iOS Safari from
 * hijacking the gesture for page scroll.
 */

interface RefineOverlayProps {
  itemId: number;
  initialBbox: Bbox;
  transform: ImageTransform;
  containerRef: React.RefObject<HTMLElement | null>;
  mode: 'box' | 'points';
  /** Image-space include/exclude points for SAM3. */
  points: AnnotationPoint[];
  /** Label for the NEXT tap in points mode: 1=include, 0=exclude. */
  nextPointLabel: 0 | 1;
  /** Called with an image-space point when the user taps in
   *  points-mode. Parent owns the list + the 32-cap. */
  onAddPoint: (p: AnnotationPoint) => void;
  /** Mirror of the hook's `box`. Parent reads this to commit on
   *  Apply. */
  onChange: (box: Bbox, dirty: boolean) => void;
}

const POINT_CAP = 32;
const INCLUDE_COLOR = '#16a34a'; // green-600
const EXCLUDE_COLOR = '#dc2626'; // red-600

export const RefineOverlay: React.FC<RefineOverlayProps> = ({
  itemId,
  initialBbox,
  transform,
  containerRef,
  mode,
  points,
  nextPointLabel,
  onAddPoint,
  onChange,
}) => {
  const draw = useBoxDraw(initialBbox, transform, containerRef);
  const lastReportedRef = useRef<Bbox | null>(null);

  // Publish every box change up — parent uses it to re-compute dirty
  // + to commit on Apply.
  React.useEffect(() => {
    if (lastReportedRef.current && bboxShallowEqual(lastReportedRef.current, draw.box)) return;
    lastReportedRef.current = draw.box;
    onChange(draw.box, draw.dirty);
  }, [draw.box, draw.dirty, onChange]);

  if (!transform.ready) return null;
  const rect = transform.toDomBbox(draw.box);
  const palette = colorFor(itemId);

  const onHandleDown =
    (kind: DragKind) =>
    (e: GestureResponderEvent | any) => {
      const pe: PointerEventLike = {
        clientX: e.nativeEvent?.clientX ?? e.clientX,
        clientY: e.nativeEvent?.clientY ?? e.clientY,
        pointerId: e.nativeEvent?.pointerId ?? e.pointerId ?? 1,
        currentTarget: e.currentTarget,
      };
      draw.startDrag(kind, pe);
      if (typeof (e as any).stopPropagation === 'function') {
        (e as any).stopPropagation();
      }
    };

  // Tap-to-add-point in points mode. We convert clientX/Y via the
  // container's bounding rect, then the transform. Taps outside the
  // rendered image rect are ignored — otherwise SAM3 gets a point
  // in the letterbox dead zone, which the backend rejects.
  const onPointsCanvasDown = (e: any) => {
    if (mode !== 'points') return;
    if (points.length >= POINT_CAP) return;
    const el = containerRef.current;
    if (!el) return;
    const clientX = e.nativeEvent?.clientX ?? e.clientX;
    const clientY = e.nativeEvent?.clientY ?? e.clientY;
    if (typeof clientX !== 'number' || typeof clientY !== 'number') return;
    const box = el.getBoundingClientRect();
    const dom: Point = { x: clientX - box.left, y: clientY - box.top };
    const renderRect = transform.renderRect;
    if (!renderRect) return;
    // Reject clicks in the letterbox strip.
    if (
      dom.x < renderRect.x ||
      dom.x > renderRect.x + renderRect.w ||
      dom.y < renderRect.y ||
      dom.y > renderRect.y + renderRect.h
    ) return;
    const img = transform.toImage(dom);
    onAddPoint({
      x: Math.round(img.x),
      y: Math.round(img.y),
      label: nextPointLabel,
    });
    if (typeof (e as any).stopPropagation === 'function') {
      (e as any).stopPropagation();
    }
  };

  // Handle placement — expressed as fractions of the bbox's DOM rect.
  const handles: { kind: DragKind; cx: number; cy: number; cursor: string }[] = [
    { kind: 'resize-nw', cx: 0, cy: 0, cursor: 'nwse-resize' },
    { kind: 'resize-n', cx: 0.5, cy: 0, cursor: 'ns-resize' },
    { kind: 'resize-ne', cx: 1, cy: 0, cursor: 'nesw-resize' },
    { kind: 'resize-e', cx: 1, cy: 0.5, cursor: 'ew-resize' },
    { kind: 'resize-se', cx: 1, cy: 1, cursor: 'nwse-resize' },
    { kind: 'resize-s', cx: 0.5, cy: 1, cursor: 'ns-resize' },
    { kind: 'resize-sw', cx: 0, cy: 1, cursor: 'nesw-resize' },
    { kind: 'resize-w', cx: 0, cy: 0.5, cursor: 'ew-resize' },
  ];

  const isBoxMode = mode === 'box';

  return (
    <View pointerEvents="box-none" style={StyleSheet.absoluteFill}>
      {/* Full-area point-capture canvas, ONLY in points mode. Sits
          under the bbox + dots so they stay hit-first. */}
      {mode === 'points' ? (
        <View
          {...({
            onPointerDown: onPointsCanvasDown,
            style: [
              StyleSheet.absoluteFill,
              {
                touchAction: 'none',
                cursor: points.length >= POINT_CAP ? 'not-allowed' : 'crosshair',
              },
            ],
          } as any)}
        />
      ) : null}

      {/* Bbox body. In box-mode this is the drag handle; in points-
          mode it renders as a read-only outline so the user sees the
          constraint area. */}
      <View
        {...({
          onPointerDown: isBoxMode ? onHandleDown('move') : undefined,
          style: [
            styles.body,
            {
              left: rect.x,
              top: rect.y,
              width: rect.w,
              height: rect.h,
              borderColor: palette.solid,
              backgroundColor: isBoxMode ? palette.faint : 'transparent',
              touchAction: 'none',
              cursor: isBoxMode
                ? draw.isDragging
                  ? 'grabbing'
                  : 'grab'
                : 'default',
              // Let clicks pass through to the points-capture canvas
              // when we're in points mode.
              ...(isBoxMode ? null : { pointerEvents: 'none' as const }),
            },
          ],
        } as any)}
      />

      {/* 8 handles — box-mode only. */}
      {isBoxMode
        ? handles.map((h) => {
            const x = rect.x + rect.w * h.cx;
            const y = rect.y + rect.h * h.cy;
            return (
              <View
                key={`handle-${h.kind}`}
                {...({
                  onPointerDown: onHandleDown(h.kind),
                  style: [
                    styles.handleHit,
                    {
                      left: x - 14,
                      top: y - 14,
                      cursor: h.cursor,
                      touchAction: 'none',
                    },
                  ],
                } as any)}
              >
                <View
                  style={[
                    styles.handleDot,
                    { borderColor: palette.solid },
                  ]}
                />
              </View>
            );
          })
        : null}

      {/* Point dots — render over everything so taps on a dot don't
          add a new overlapping point. Each dot is +/- colored. */}
      {points.map((p, idx) => {
        const dom = transform.toDom({ x: p.x, y: p.y });
        const color = p.label === 1 ? INCLUDE_COLOR : EXCLUDE_COLOR;
        return (
          <View
            key={`pt-${idx}-${p.x}-${p.y}`}
            pointerEvents="none"
            style={[
              styles.pointDot,
              {
                left: dom.x - 10,
                top: dom.y - 10,
                backgroundColor: color,
              },
            ]}
          >
            <View style={styles.pointGlyph}>
              {/* Unicode +/− renders tight across platforms; avoids a
                  Text font-metric micro-offset. */}
              <View
                style={[
                  styles.pointBar,
                  { backgroundColor: '#fff' },
                ]}
              />
              {p.label === 1 ? (
                <View
                  style={[
                    styles.pointBar,
                    styles.pointBarV,
                    { backgroundColor: '#fff' },
                  ]}
                />
              ) : null}
            </View>
          </View>
        );
      })}
    </View>
  );
};

const bboxShallowEqual = (a: Bbox, b: Bbox): boolean =>
  a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;

const styles = StyleSheet.create({
  body: {
    position: 'absolute',
    borderWidth: 2,
    borderRadius: 4,
  },
  handleHit: {
    position: 'absolute',
    width: 28,
    height: 28,
    justifyContent: 'center',
    alignItems: 'center',
  },
  handleDot: {
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#FFFFFF',
    borderWidth: 2,
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
        } as any)
      : {}),
  },
  pointDot: {
    position: 'absolute',
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#fff',
    justifyContent: 'center',
    alignItems: 'center',
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: '0 2px 6px rgba(0,0,0,0.45)',
        } as any)
      : {}),
  },
  pointGlyph: {
    width: 12,
    height: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pointBar: {
    position: 'absolute',
    width: 8,
    height: 2,
    borderRadius: 1,
  },
  pointBarV: {
    width: 2,
    height: 8,
  },
});
